import styled from 'styled-components';
import { useState } from 'react';

import { th } from '@yoweb/ui/styles/utils/theme';
import { Flex } from '@yoweb/ui/components/Flex';
import { StarIcon } from '@yoweb/ui/components/Icon/StarIcon';
import { useTranslation } from '@yoweb/next-i18next';
import type { FlexProps } from '@yoweb/ui/styles/utils/mixins.utils';
import type { IconProps } from '@yoweb/ui/components/Icon/IconBase';

type StarComponentProps = {
  activeHoverIndex: number | null;
  currentValue: StarPickerProps['value'];
  disabled: StarPickerProps['disabled'];
  star: Star;
  size: StarPickerProps['size'];
  onBlur: (star: Star) => void;
  onChange: StarPickerProps['onChange'];
  onFocus: (star: Star) => void;
  onMouseOver: (star: Star) => void;
};

const StarComponent = ({
  activeHoverIndex,
  currentValue,
  disabled,
  star,
  size,
  onBlur,
  onChange,
  onFocus,
  onMouseOver,
}: StarComponentProps) => {
  const { t } = useTranslation('star-rating');

  const isHighlighted =
    activeHoverIndex !== null
      ? activeHoverIndex >= star.value
      : typeof currentValue === 'number' && currentValue >= star.value;

  return (
    <TransparentButton
      role="radio"
      disabled={disabled}
      aria-checked={star.isSelected}
      aria-label={t('aria-label', { value: star.value })}
      data-testid={`star-picker-${star.value}`}
      onBlur={() => onBlur(star)}
      onClick={(event) => {
        event.stopPropagation();
        onChange(star.value);
      }}
      onFocus={() => onFocus(star)}
      onMouseOver={() => onMouseOver(star)}
      type="button"
    >
      <StarIconWithEffects size={size} isHighlighted={isHighlighted} />
    </TransparentButton>
  );
};

type Star = {
  value: number;
  isSelected: boolean;
};

const createStars = (count: number, currentValue: number | undefined | null): Star[] => {
  const arr = [];

  for (let value = 1; value <= count; value++) {
    arr.push({ value, isSelected: value === currentValue });
  }

  return arr;
};

export type StarPickerProps = {
  count: number;
  disabled?: boolean;
  gap?: FlexProps['gap'];
  size?: IconProps['size'];
  value: number | null | undefined;
  onChange: (nextValue: number) => void;
};

export const StarPicker = ({
  count,
  disabled,
  gap = 'normal1',
  size = '40',
  value,
  onChange,
}: StarPickerProps) => {
  const [activeHoverIndex, setActiveHoverIndex] = useState<number | null>(null);

  const stars = createStars(count, value);
  const resetHoverIndex = () => setActiveHoverIndex(null);
  const setHoverIndex = (star: Star) => setActiveHoverIndex(star.value);

  return (
    <Flex
      gap={gap}
      role="radiogroup"
      data-testid="star-picker"
      onBlur={resetHoverIndex}
      onMouseLeave={resetHoverIndex}
    >
      {stars.map((star) => (
        <StarComponent
          key={star.value}
          activeHoverIndex={activeHoverIndex}
          currentValue={value}
          disabled={disabled}
          star={star}
          size={size}
          onChange={onChange}
          onBlur={resetHoverIndex}
          onFocus={setHoverIndex}
          onMouseOver={setHoverIndex}
        />
      ))}
    </Flex>
  );
};

const TransparentButton = styled.button<{ disabled?: boolean }>`
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const StarIconWithEffects = styled(StarIcon)<{ isHighlighted: boolean }>`
  && {
    color: ${({ isHighlighted }) =>
      isHighlighted ? th.getColor('gingko') : th.getColor('base000')};
    transition: transform, opacity;
    transition-duration: ${th.getDuration('interaction')}ms;
    cursor: pointer;

    &:hover {
      color: ${th.getColor('gingko')};
      transform: translate3d(0, 0, 0) scale(1.2);
    }
  }
`;
